import { defineStore } from 'pinia'

interface GeoLocation {
  latitude: number
  longitude: number
}

interface Tokens {
  access: {
    token: string | null
    expires: Date | null
  }
  refresh: {
    token: string | null
    expires: Date | null
  }
}

interface AuthState {
  callTimer: ReturnType<typeof setTimeout> | null
  callInterval: ReturnType<typeof setInterval> | null
  callId: number | null
  callback: string | null
  pwaInstallEnd: boolean
  phone: number
  driverId: string
  driverId1c: string
  driverName: string
  accountId: string | null
  authenticated: boolean
  codeRequested: boolean
  loading: boolean
  geolocation: GeoLocation
  tokens: Tokens
  readNews: string[]
  readApplication: string[]
  readService: string[]
  geoAccept: boolean
  tags: string[]
}

export const useAuthStore = defineStore<
  'auth',
  AuthState,
  {},
  {
    saveSettings: () => Promise<void>
    loadSettings: () => Promise<void>
    authReqPhone: (phone: number) => Promise<void>
    authCallPhone: () => Promise<void>
    authCallPhoneResult: () => Promise<void>
    authReqCode: (code: number) => Promise<void>
    logUserOut: () => void
  }
>('auth', {
  state: (): AuthState => ({
    callTimer: null,
    callInterval: null,
    callId: null,
    callback: null,
    pwaInstallEnd: false,
    phone: 0,
    driverId: '',
    driverId1c: '',
    driverName: '',
    accountId: null,
    authenticated: false,
    codeRequested: false,
    loading: false,
    geolocation: {
      latitude: 0,
      longitude: 0,
    },
    tokens: {
      access: {
        token: null,
        expires: null,
      },
      refresh: {
        token: null,
        expires: null,
      },
    },
    readNews: [],
    readApplication: [],
    readService: [],
    geoAccept: true,
    tags: [],
  }),
  actions: {
    async saveSettings() {
      const config = useRuntimeConfig()
      const token = useCookie('token_refresh')

      const { data }: any = await useFetch(config.public.serverApi + '/api/v1/driver/settings/set', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: {
          token,
          settings: {
            readNews: this.readNews,
            readApplication: this.readApplication,
            readService: this.readService,
            geoAccept: this.geoAccept,
          },
        },
      })
    },

    async loadSettings() {
      const config = useRuntimeConfig()
      const token = useCookie('token_refresh')

      const { data }: any = await useFetch(config.public.serverApi + '/api/v1/driver/settings/get', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: {
          token,
        },
      })

      if (data.value) {
        this.readNews = data.value.readNews
        this.readApplication = data.value.readApplication
        this.readService = data.value.readService
        this.geoAccept = data.value.geoAccept
      }
    },

    async authCallPhoneResult() {
      const config = useRuntimeConfig()

      if (this.driverId) {
        // @ts-ignore
        const { data }: any = await useFetch(config.public.serverApi + '/api/v1/driver/login/phone/check', {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: {
            driver_id: this.driverId,
            call_id: this.callId,
          },
        })

        if (data.value?.status === false) {
          this.authenticated = false
          const error = data.value?.error ?? 'ERR-01. Неизвестная ошибка'
          push.error(error)
        }

        if (data.value?.status === 'ok') {
          this.loading = false
          this.callTimer = null
          if (this.callInterval) clearInterval(this.callInterval)

          this.authenticated = true
          this.tokens = data.value.tokens

          const tokenAccess = useCookie('token_access')
          const tokenRefresh = useCookie('token_refresh')

          tokenAccess.value = this.tokens.access.token
          tokenRefresh.value = this.tokens.refresh.token
        } else if (data.value?.status === 'error' && data.value?.errtype === 'timeout') {
          this.loading = false
          this.callTimer = null
          if (this.callInterval) clearInterval(this.callInterval)
          push.error('CALL ID истёк по сроку действия. Попробуйте ещё раз.')
        }
      } else {
        push.error('ERR-02. Driver ID не найден.')
      }
    },

    async authCallPhone() {
      const config = useRuntimeConfig()

      // @ts-ignore
      const { data }: any = await useFetch(config.public.serverApi + '/api/v1/driver/login/phone/getphone', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: {
          phone: this.phone,
        },
      })

      if (data.value?.callback) {
        this.callId = data.value.call_id
        this.callback = data.value.callback
      } else {
        push.error('Не выдан номер для авторизации.')
      }
    },

    async authReqPhone(phone: number) {
      const config = useRuntimeConfig()

      if (phone) {
        this.phone = phone
        // @ts-ignore
        const { data }: any = await useFetch(config.public.serverApi + '/api/v1/driver/login/phone', {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: {
            phone,
          },
        })

        if (data.value?.status === null) {
          push.error('Номер не найден в базе!')
        }

        if (data.value?.status === 'ok') {
          this.authenticated = false
          this.codeRequested = true
          this.driverId = data.value.driver_id
          this.accountId = data.value.account_id
          push.success('Код успешно запрошен')
        }
      }
    },

    async authReqCode(code: number) {
      const config = useRuntimeConfig()

      if (code && this.driverId) {
        // @ts-ignore
        const { data }: any = await useFetch(config.public.serverApi + '/api/v1/driver/login/code', {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: {
            driver_id: this.driverId,
            code,
          },
        })

        if (data.value?.status === false) {
          this.authenticated = false
          const error = data.value?.error ?? 'ERR-01. Неизвестная ошибка'
          push.error(error)
        }

        if (data.value?.status === 'ok') {
          this.authenticated = true
          this.codeRequested = false
          this.tokens = data.value.tokens

          const tokenAccess = useCookie('token_access')
          const tokenRefresh = useCookie('token_refresh')

          tokenAccess.value = this.tokens.access.token
          tokenRefresh.value = this.tokens.refresh.token
        }
      } else {
        push.error('ERR-02. Driver ID не найден.')
      }
    },

    logUserOut() {
      const token = useCookie('token')
      this.$reset()
      token.value = null
    },
  },
  // @ts-ignore
  persist: true,
})
